import { useEffect } from 'react';

import type { Identity } from '../../auth/domain/resolve-identity.js';
import { foregroundDrainer } from '../application/foreground-drainer-instance.js';

/** Foreground only — no point polling a backgrounded/hidden tab (coordinator finding #1). */
const PERIODIC_TICK_MS = 30_000;

/**
 * useForegroundDrainerTriggers (coordinator finding #1) — mounts the
 * boot/online/periodic triggers for the minimal foreground drainer. The
 * post-enqueue trigger lives separately, in `CheckinPage`'s own
 * `submitCreate` (right after a successful `enqueueCheckin()`), since it
 * needs to fire from that specific call site, not a mount effect.
 *
 * Mount once, high in the authenticated tree (`_authenticated.tsx`'s
 * `AuthenticatedLayout`) — every trigger calls `.tick()` on the SAME
 * `foregroundDrainer` singleton also used by CheckinPage, so the drainer's
 * own in-tab single-flight guard actually covers every trigger source.
 *
 * `hasSession` (CRITICAL fix, adversarial review) — whether a LIVE session
 * currently backs `identity`, as opposed to the offline profile
 * (`resolveIdentity()` cannot tell the two apart; it returns the identical
 * `{orgId, capturedByUserId}` shape either way for the SAME operator).
 * `useOfflineShellRecovery` can, WITHOUT a page reload, turn an
 * offline-shell render (no session) into a live-session render for the
 * exact same org/user — the mount-tick effect below depends on
 * [orgId, capturedByUserId] alone, so that transition would never
 * re-trigger a tick, and any item the earlier session-less requests had
 * already classified `needs-auth` would stay stuck until a manual reload.
 * Including `hasSession` here forces a fresh tick on that transition too.
 */
export function useForegroundDrainerTriggers(identity: Identity | null, hasSession: boolean): void {
  // Destructured to stable primitives BEFORE the effect: `identity` itself
  // is a freshly-computed object on every render (resolveIdentity() has no
  // memoization), so depending on it directly would re-run this effect
  // every render instead of only when the actual org/operator changes.
  const orgId = identity?.orgId;
  const capturedByUserId = identity?.capturedByUserId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: hasSession is an intentional re-tick trigger (offline-profile → live-session transition, see hook doc comment above), not a value read inside the effect body.
  useEffect(() => {
    if (!orgId || !capturedByUserId) return;
    const currentIdentity: Identity = { orgId, capturedByUserId };

    // Boot / identity-just-resolved trigger.
    void foregroundDrainer.tick(currentIdentity);

    const onOnline = () => void foregroundDrainer.tick(currentIdentity);
    window.addEventListener('online', onOnline);

    const interval = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) {
        void foregroundDrainer.tick(currentIdentity);
      }
    }, PERIODIC_TICK_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(interval);
    };
  }, [orgId, capturedByUserId, hasSession]);
}
